import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { EASE_EXPO, VIEWPORT } from "@/lib/motion";
import { TextReveal } from "@/components/ui/TextReveal";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

const reviews = [
  {
    quote:
      "Absolutely loved the Mini Cookies, Cookie Tin, and Chocolate Cookie Bar. Perfect sweetness, amazing freshness, and excellent quality in every bite. Highly recommended for anyone who loves delicious baked treats.",
    name: "Akshay S.",
    where: "Hadapsar, Pune",
    rating: 5,
  },
  {
    quote:
      "Hands down to the best guilt-free treats!😇 It’s so rare to find baked goods that are genuinely healthy without compromising on taste. The tin cakes are incredibly moist and perfectly sweetened, and the cookies have the most delightful crunch. Highly recommended!",
    name: "Rucha S.",
    where: "Karad",
    rating: 5,
  },
  {
    quote:
      "The Naankatai and marble mini cookies are delicious. Unique flavors and textures that are hard to find elsewhere.",
    name: "Bina P.",
    where: "Dabhol, Ratnagiri",
    rating: 5,
  },
  {
    quote:
      "The butter cake was delicious with a perfect texture. The chocolate chip cookies were soft and melts in your mouth with a rich chocolate flavor.",
    name: "Riya P.",
    where: "Pune",
    rating: 5,
  },
];

const Card = ({ r, i }) => (
  <motion.figure
    data-testid={`testimonial-${i}`}
    initial={{ opacity: 0, y: 14 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={VIEWPORT}
    transition={{ duration: 0.38, delay: (i % 3) * 0.05, ease: EASE_EXPO }}
    className="relative bg-[#FAF6F0] rounded-[22px] p-5 sm:p-6 md:p-7 border border-[#3B2818]/10 h-full flex flex-col justify-between shadow-xs hover:shadow-md transition-shadow duration-300"
  >
    <Quote className="absolute top-5 right-5 h-6 w-6 text-[#C06A22]/15 pointer-events-none" />
    
    <div>
      <div className="flex items-center gap-1 mb-3 text-[#C06A22]">
        {[...Array(r.rating || 5)].map((_, idx) => (
          <Star key={idx} className="h-4 w-4 fill-current" />
        ))}
      </div>
      <blockquote className="font-serif text-base sm:text-lg leading-relaxed text-[#3B2818] italic">
        &ldquo;{r.quote}&rdquo;
      </blockquote>
    </div>

    <figcaption className="mt-5 pt-4 border-t border-[#3B2818]/10 flex items-center justify-between text-xs uppercase tracking-[0.14em] text-[#6B5343]">
      <div className="flex items-center gap-2.5">
        <div className="w-7 h-7 rounded-full bg-[#C06A22]/15 text-[#C06A22] font-semibold flex items-center justify-center text-xs">
          {r.name.charAt(0)}
        </div>
        <span className="font-semibold text-[#3B2818] lowercase first-letter:capitalize">{r.name}</span>
      </div>
      <span className="text-[11px] text-[#6B5343]/80">{r.where}</span>
    </figcaption>
  </motion.figure>
);

export default function Testimonials() {
  const [api, setApi] = useState(null);
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isHeld, setIsHeld] = useState(false);

  useEffect(() => {
    if (!api) return;

    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());

    const onSelect = () => {
      setCurrent(api.selectedScrollSnap());
    };

    api.on("select", onSelect);
    return () => {
      api.off("select", onSelect);
    };
  }, [api]);

  useEffect(() => {
    if (!api || isHovered || isHeld) return;

    // Auto-scroll pace set to 3 seconds for brisk, good speed
    const timer = setInterval(() => {
      api.scrollNext();
    }, 3000);

    return () => clearInterval(timer);
  }, [api, isHovered, isHeld]);

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="relative py-10 sm:py-14 md:py-16 overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsHovered(true)}
      onBlur={() => setIsHovered(false)}
      onPointerDown={() => setIsHeld(true)}
      onPointerUp={() => setIsHeld(false)}
      onPointerCancel={() => setIsHeld(false)}
    >
      <div className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-12">
        <Carousel
          setApi={setApi}
          opts={{
            align: "start",
            loop: true,
          }}
          className="w-full relative"
        >
          <div className="mb-6 sm:mb-8 flex flex-col md:flex-row md:items-end justify-between gap-5">
            <div>
              <span className="text-[11px] uppercase tracking-[0.26em] text-[#C06A22] font-semibold">
                Customer love
              </span>
              <TextReveal className="font-serif text-2xl sm:text-4xl md:text-5xl leading-tight tracking-tight mt-1.5 text-[#3B2818] max-w-xl">
                The notes people send back with the crumbs.
              </TextReveal>
            </div>

            <div className="hidden sm:flex items-center gap-2.5">
              <CarouselPrevious className="static translate-y-0 h-10 w-10 rounded-full border border-[#3B2818]/20 bg-[#F2EBE0] text-[#3B2818] hover:bg-[#3B2818] hover:text-[#FAF6F0] transition-colors duration-200" />
              <CarouselNext className="static translate-y-0 h-10 w-10 rounded-full border border-[#3B2818]/20 bg-[#F2EBE0] text-[#3B2818] hover:bg-[#3B2818] hover:text-[#FAF6F0] transition-colors duration-200" />
            </div>
          </div>

          <CarouselContent className="-ml-4 md:-ml-5 py-1.5">
            {reviews.map((r, i) => (
              <CarouselItem
                key={i}
                className="pl-4 md:pl-5 basis-full sm:basis-1/2 lg:basis-1/3"
              >
                <Card r={r} i={i} />
              </CarouselItem>
            ))}
          </CarouselContent>

          {/* Controls & Dots Pagination */}
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3.5">
            <span className="text-xs uppercase tracking-[0.18em] text-[#3B2818]/60 font-medium text-center sm:text-left">
              Swipe or use arrows to explore • {reviews.length} reviews
            </span>

            {/* Indicator Dots */}
            {count > 0 && (
              <div className="flex items-center gap-2">
                {Array.from({ length: count }).map((_, index) => (
                  <button
                    key={index}
                    aria-label={`Go to review slide ${index + 1}`}
                    onClick={() => api?.scrollTo(index)}
                    className={`h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
                      current === index
                        ? "w-7 bg-[#C06A22]"
                        : "w-2.5 bg-[#3B2818]/20 hover:bg-[#3B2818]/40"
                    }`}
                  />
                ))}
              </div>
            )}

            <div className="flex sm:hidden items-center gap-2.5">
              <CarouselPrevious className="static translate-y-0 h-9 w-9 rounded-full border border-[#3B2818]/20 bg-[#F2EBE0] text-[#3B2818] hover:bg-[#3B2818] hover:text-[#FAF6F0]" />
              <CarouselNext className="static translate-y-0 h-9 w-9 rounded-full border border-[#3B2818]/20 bg-[#F2EBE0] text-[#3B2818] hover:bg-[#3B2818] hover:text-[#FAF6F0]" />
            </div>
          </div>
        </Carousel>
      </div>
    </section>
  );
}


